import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiUsers, FiCheck, FiEye, FiEyeOff, FiArrowLeft } from 'react-icons/fi';
import { loginMember, registerMember, resendVerification } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import styles from './LoginRegister.module.css';

const FEATURES = [
  'Multi-Derasar donation tracking',
  'Donation offering history',
  'Family contribution records',
  'Financial year reporting',
  'View notices & events',
];

const MemberAuth = () => {
  const { loginUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [tab, setTab]                       = useState(() => searchParams.get('tab') === 'register' ? 'register' : 'login');
  const [loading, setLoading]               = useState(false);
  const [error, setError]                   = useState('');
  const [unverifiedUser, setUnverifiedUser] = useState(null);
  const [resendLoading, setResendLoading]   = useState(false);
  const [showPassword, setShowPassword]     = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [form, setForm] = useState({ name: '', identifier: '', phone: '', password: '', confirmPassword: '' });

  const handleChange = (e) => {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      if (tab === 'login') {
        const res = await loginMember({ identifier: form.identifier, password: form.password });
        if (!res.data.isEmailVerified) {
          loginUser(res.data);
          setUnverifiedUser(res.data);
          return;
        }
        loginUser(res.data);
        navigate('/dashboard');
      } else {
        if (form.password !== form.confirmPassword) {
          setError('Passwords do not match.');
          setLoading(false);
          return;
        }
        const res = await registerMember({ name: form.name, email: form.identifier, phone: form.phone, password: form.password });
        loginUser(res.data);
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.errors?.[0]?.msg || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setResendLoading(true);
    try {
      await resendVerification();
      toast.success('Verification email sent! Please check your inbox.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to resend. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className={styles.page}>

      {/* ── Left panel — warm orange ── */}
      <div className={styles.leftPanel}>
        <div className={styles.brandBadge}>
          <FiUsers size={11} /> Member Portal
        </div>
        <div className={styles.brandName}>Community<br />Member Portal</div>
        <p className={styles.brandTagline}>
          Join your Jain Derasar, record donations, manage your family, and track your complete contribution history.
        </p>
        <div className={styles.features}>
          {FEATURES.map(f => (
            <div key={f} className={styles.featureItem}>
              <div className={styles.featureIcon}><FiCheck size={13} /></div>
              {f}
            </div>
          ))}
        </div>
      </div>

      {/* ── Right panel — form ── */}
      <div className={styles.rightPanel}>
        <div className={styles.formContainer}>

          <Link to="/" className={styles.backLink}>
            <FiArrowLeft size={13} /> Back to home
          </Link>

          <h2 className={styles.formTitle}>
            {tab === 'login' ? 'Member Sign In' : 'Create Member Account'}
          </h2>
          <p className={styles.formSubtitle}>
            {tab === 'login' ? 'Welcome back — sign in to your account' : 'Join your Derasar community'}
          </p>

          <div className={styles.authTabs}>
            {[{ id: 'login', label: 'Sign In' }, { id: 'register', label: 'Register' }].map(t => (
              <button
                key={t.id}
                type="button"
                className={`${styles.authTab} ${tab === t.id ? styles.active : ''}`}
                onClick={() => { setTab(t.id); setError(''); }}
              >
                {t.label}
              </button>
            ))}
          </div>

          {unverifiedUser && (
            <div className={styles.verifyBanner}>
              <strong>Please verify your email</strong>
              <p>We sent a verification link to <strong>{unverifiedUser.email}</strong>. You must verify before accessing all features.</p>
              <div className={styles.verifyBannerActions}>
                <button type="button" className="btn btn-primary btn-sm" onClick={handleResendVerification} disabled={resendLoading}>
                  {resendLoading ? 'Sending…' : 'Resend Email'}
                </button>
              </div>
            </div>
          )}

          {!unverifiedUser && error && <div className={styles.errorMsg}>{error}</div>}

          {!unverifiedUser && (
            <form onSubmit={handleSubmit}>
              {tab === 'register' && (
                <div className="form-group">
                  <label>Full Name *</label>
                  <input name="name" value={form.name} onChange={handleChange} placeholder="e.g. Rahul Shah" required />
                </div>
              )}

              <div className="form-group">
                <label>{tab === 'login' ? 'Email or Phone Number *' : 'Email Address *'}</label>
                <input
                  name="identifier"
                  type={tab === 'register' ? 'email' : 'text'}
                  value={form.identifier}
                  onChange={handleChange}
                  placeholder={tab === 'login' ? 'Enter email or phone number' : 'Enter email address'}
                  required
                />
                {tab === 'login' && (
                  <small style={{ color: 'var(--color-text-muted)', fontSize: '0.75rem' }}>
                    You can sign in with either your email or mobile number
                  </small>
                )}
              </div>

              {tab === 'register' && (
                <div className="form-group">
                  <label>Mobile Number *</label>
                  <input name="phone" type="tel" value={form.phone} onChange={handleChange} placeholder="Enter 10-digit mobile number" required />
                </div>
              )}

              <div className="form-group" style={{ position: 'relative' }}>
                <label>Password *</label>
                <input
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={form.password}
                  onChange={handleChange}
                  required
                  minLength={6}
                  style={{ paddingRight: '40px' }}
                />
                <span
                  onClick={() => setShowPassword(s => !s)}
                  style={{ position: 'absolute', right: 12, top: 36, cursor: 'pointer', color: 'var(--color-text-muted)' }}
                >
                  {showPassword ? <FiEye /> : <FiEyeOff />}
                </span>
                {tab === 'register' && form.password.length > 0 && form.password.length < 6 && (
                  <small style={{ color: '#dc2626', fontSize: '0.75rem', display: 'block', marginTop: 4 }}>
                    Password must be at least 6 characters
                  </small>
                )}
              </div>

              {tab === 'register' && (
                <div className="form-group" style={{ position: 'relative' }}>
                  <label>Confirm Password *</label>
                  <input
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={form.confirmPassword}
                    onChange={handleChange}
                    required
                    minLength={6}
                    style={{ paddingRight: '40px' }}
                  />
                  <span
                    onClick={() => setShowConfirmPassword(s => !s)}
                    style={{ position: 'absolute', right: 12, top: 36, cursor: 'pointer', color: 'var(--color-text-muted)' }}
                  >
                    {showConfirmPassword ? <FiEye /> : <FiEyeOff />}
                  </span>
                  {form.confirmPassword.length > 0 && form.confirmPassword !== form.password && (
                    <small style={{ color: '#dc2626', fontSize: '0.75rem', display: 'block', marginTop: 4 }}>
                      Passwords do not match
                    </small>
                  )}
                </div>
              )}

              {tab === 'login' && (
                <div style={{ textAlign: 'right' }}>
                  <Link to="/forgot-password" className={styles.forgotLink}>Forgot password?</Link>
                </div>
              )}

              <button type="submit" className={styles.submitBtn} disabled={loading}>
                {loading ? 'Please wait…' : tab === 'login' ? 'Sign In' : 'Create Account'}
              </button>
            </form>
          )}

          <div className={styles.switchLink}>
            Are you a Derasar Admin?{' '}
            <Link to="/login/admin">Admin login ?</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MemberAuth;
